import { Component, HostListener, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { CalculatorTypes } from './enums/calculator-types.enum';
import { CalculatorInput } from './interfaces/calculator-input.interface';
import { CalculatorHelpers } from './helpers/calculator-helpers';

@Component({
  selector: 'gtd-calculator',
  templateUrl: './calculator.component.html',
})
export class CalculatorComponent implements OnInit {
  @Input() metrics: { id: number; name: string }[];
  @Input() initialFormula: string;
  @Input() formulaInvalid: boolean;
  @Input() inputFocused: boolean;
  @Output() formulaChange = new EventEmitter<string>();
  public numbers: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  public inputs: CalculatorInput[] = [];
  public previous: CalculatorTypes;
  public Types = CalculatorTypes;

  public selectedIndex: number = null;

  public isNumbersDisabled: boolean;
  public isOpenBracketDisabled: boolean;
  public isCloseBracketDisabled: boolean;
  public isOperatorsDisabled: boolean;
  public isMetricsDisabled: boolean;

  public calculatorHelpers = CalculatorHelpers;

  public operators: { [key: string]: string } = {
    '*': '×',
    '/': '÷',
    '+': '+',
    '-': '-',
  };

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (this.inputFocused) {
      return;
    }
    this.cd.markForCheck();
    if (!this.isNumbersDisabled && this.numbers.includes(event.key)) {
      this.addValue(event.key, this.Types.NUMBER);
    }

    if (!this.isOperatorsDisabled) {
      if (this.operators[event.key]) {
        this.addValue(event.key, this.Types.OPERATOR);
      }
    }
    if (event.key === '(' && !this.isOpenBracketDisabled) {
      this.addValue('(', this.Types.OPEN_BRACKET);
    }
    if (event.key === ')' && !this.isCloseBracketDisabled) {
      this.addValue(')', this.Types.CLOSE_BRACKET);
    }

    if (this.inputs.length && event.key === 'Backspace') {
      this.backspace();
    }

    if (event.key === 'ArrowRight') {
      if (this.selectedIndex !== null && this.selectedIndex < this.inputs.length - 1) {
        this.selectedIndex++;
        this.selectInput(this.selectedIndex);
      }
    }
    if (event.key === 'ArrowLeft') {
      if (this.selectedIndex !== null && this.selectedIndex >= 0) {
        this.selectedIndex--;
        this.selectInput(this.selectedIndex);
      } else if (this.selectedIndex === null && this.inputs.length > 2) {
        this.selectedIndex = this.inputs.length - 2;
        this.selectInput(this.selectedIndex);
      }
    }

    this.updateDisabledStates();
  }

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.initialFormula?.length) {
      this.inputs = this.calculatorHelpers.parseFormula(this.initialFormula, this.metrics);
      this.previous = this.inputs[this.inputs.length - 1].type;
    }
    this.updateDisabledStates();
  }

  public addValue(value: string, type: CalculatorTypes, event?: any, id?: number): void {
    event?.preventDefault();

    const index = this.selectedIndex ?? this.inputs.length - 1;

    if (type === this.Types.NUMBER && this.previous === this.Types.NUMBER) {
      this.inputs[index].value += value;
    } else if (this.selectedIndex !== null) {
      this.inputs.splice(++this.selectedIndex, 0, { type, value, id });
    } else {
      this.inputs.push({ type, value, id });
    }

    this.previous = type;
    this.updateFormula();
    this.updateDisabledStates();
  }

  updateFormula(): void {
    let formula = '';
    this.inputs.forEach((input) => {
      if (input.type !== this.Types.METRIC) {
        formula += input.value;
      } else {
        formula += `{metric_${input.id}}`;
      }
    });
    this.formulaChange.emit(formula);
  }

  public backspace(event?: any): void {
    event?.preventDefault();

    const index = this.selectedIndex ?? this.inputs.length - 1;
    if (this.previous === this.Types.NUMBER && this.inputs[index]?.value?.length > 1) {
      this.inputs[index].value = this.inputs[index].value.slice(0, -1);
    } else {
      this.inputs.pop();
      if (this.selectedIndex) {
        if (this.selectedIndex > 0) {
          this.selectInput(this.selectedIndex - 1);
        } else {
          this.selectedIndex--;
          this.previous = null;
        }
      } else {
        this.previous = this.inputs[index - 1]?.type ?? null;
      }
    }

    this.updateFormula();
    this.updateDisabledStates();
  }

  public clear(event: any): void {
    event?.preventDefault();

    this.inputs = [];
    this.previous = null;
    this.updateFormula();
    this.updateDisabledStates();
  }

  selectInput(index: number): void {
    this.selectedIndex = index;
    this.previous = this.inputs[index]?.type ?? null;
    this.updateDisabledStates();
  }

  selectLast(): void {
    this.selectedIndex = null;
    this.previous = this.inputs[this.inputs.length - 1].type;
    this.updateDisabledStates();
  }

  updateDisabledStates(): void {
    this.isNumbersDisabled = [this.Types.CLOSE_BRACKET, this.Types.METRIC].includes(this.previous);
    this.isOperatorsDisabled = !this.previous || [this.Types.OPERATOR, this.Types.OPEN_BRACKET].includes(this.previous);
    this.isOpenBracketDisabled = [this.Types.NUMBER, this.Types.CLOSE_BRACKET, this.Types.METRIC].includes(this.previous);
    this.isCloseBracketDisabled = !this.previous || [this.Types.OPERATOR, this.Types.OPEN_BRACKET].includes(this.previous);
    this.isMetricsDisabled = [this.Types.METRIC, this.Types.NUMBER, this.Types.CLOSE_BRACKET].includes(this.previous);
    this.cd.markForCheck();
  }
}
